import { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { MapPin, Crosshair, Coins, Plus, Lightning } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const getH = () => ({});

// Fix default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const boltzIcon = new L.DivIcon({
    html: '<div style="background:#FFD700;width:24px;height:24px;border-radius:50%;border:2px solid #000;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:bold;color:#000">B</div>',
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    className: '',
});

const portalIcon = new L.DivIcon({
    html: '<div style="background:#7A00FF;width:28px;height:28px;border-radius:50%;border:2px solid #fff;display:flex;align-items:center;justify-content:center;font-size:14px;color:#fff;animation:pulse 2s infinite">P</div>',
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    className: '',
});

const riderDropIcon = new L.DivIcon({
    html: '<div style="background:#1E90FF;width:22px;height:22px;border-radius:50%;border:2px solid #fff;display:flex;align-items:center;justify-content:center;font-size:11px;color:#fff">R</div>',
    iconSize: [22, 22],
    iconAnchor: [11, 11],
    className: '',
});

function getIcon(type) {
    if (type === 'portal') return portalIcon;
    if (type === 'rider_drop') return riderDropIcon;
    return boltzIcon;
}

function LocationMarker({ position, setPosition }) {
    useMapEvents({
        click(e) { setPosition([e.latlng.lat, e.latlng.lng]); },
    });
    return position ? <Marker position={position}><Popup>Drop location</Popup></Marker> : null;
}

export default function ARWaypoints() {
    const [waypoints, setWaypoints] = useState([]);
    const [myCollections, setMyCollections] = useState([]);
    const [userPos, setUserPos] = useState(null);
    const [dropPos, setDropPos] = useState(null);
    const [dropReward, setDropReward] = useState('5');
    const [dropTitle, setDropTitle] = useState('');
    const [dropping, setDropping] = useState(false);
    const [collecting, setCollecting] = useState(null);
    const [showDrop, setShowDrop] = useState(false);

    const collectedIds = new Set(myCollections.map(c => c.waypoint_id));

    const fetchData = useCallback(async (lat, lng) => {
        try {
            const params = lat && lng ? `?lat=${lat}&lng=${lng}&radius_km=50` : '';
            const [wpRes, colRes] = await Promise.all([
                fetch(`${API}/ar/waypoints${params}`, { credentials: 'include', headers: getH() }),
                fetch(`${API}/ar/my-collections`, { credentials: 'include', headers: getH() }),
            ]);
            if (wpRes.ok) setWaypoints(await wpRes.json());
            if (colRes.ok) setMyCollections(await colRes.json());
        } catch (e) { console.error('AR fetch:', e); }
    }, []);

    useEffect(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    const coords = [pos.coords.latitude, pos.coords.longitude];
                    setUserPos(coords);
                    fetchData(coords[0], coords[1]);
                },
                () => { fetchData(); },
                { enableHighAccuracy: true }
            );
        } else { fetchData(); }
    }, [fetchData]);

    const collectWaypoint = async (waypointId) => {
        if (!userPos) { toast.error('GPS location needed'); return; }
        setCollecting(waypointId);
        try {
            const res = await fetch(`${API}/ar/waypoints/${waypointId}/collect`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getH() },
                credentials: 'include',
                body: JSON.stringify({ lat: userPos[0], lng: userPos[1] }),
            });
            if (res.ok) {
                const result = await res.json();
                toast.success(result.message);
                fetchData(userPos[0], userPos[1]);
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Collection failed');
            }
        } catch { toast.error('Collection failed'); }
        finally { setCollecting(null); }
    };

    const dropWaypoint = async () => {
        if (!dropPos) { toast.error('Tap the map to set a drop location'); return; }
        setDropping(true);
        try {
            const res = await fetch(`${API}/ar/waypoints/drop`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getH() },
                credentials: 'include',
                body: JSON.stringify({
                    lat: dropPos[0], lng: dropPos[1],
                    reward_boltz: parseInt(dropReward) || 5,
                    title: dropTitle || undefined,
                }),
            });
            if (res.ok) {
                toast.success('Waypoint dropped!');
                setShowDrop(false); setDropPos(null); setDropTitle('');
                fetchData(userPos?.[0], userPos?.[1]);
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Drop failed');
            }
        } catch { toast.error('Drop failed'); }
        finally { setDropping(false); }
    };

    const center = userPos || [43.65, -79.38]; // Default to Toronto

    return (
        <Layout>
            <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-4">
                    <span className="label-text text-cyber-purple text-glow-purple">AR MODE</span>
                    <h1 className="text-2xl sm:text-3xl font-bold mt-1 font-display" data-testid="ar-waypoints-title">
                        AR Waypoints
                    </h1>
                    <p className="text-muted-foreground text-sm mt-1">Discover and collect Boltz drops scattered across the map</p>
                    <div className="flex gap-2 mt-3">
                        <Badge variant="outline" className="text-[10px] border-neon-gold/30 text-neon-gold">
                            <Coins size={10} className="mr-1" />{waypoints.length} Waypoints
                        </Badge>
                        <Badge variant="outline" className="text-[10px] border-neon-green/30 text-neon-green">
                            {myCollections.length} Collected
                        </Badge>
                        <Button size="sm" variant="outline" className="text-xs ml-auto"
                            onClick={() => setShowDrop(!showDrop)} data-testid="toggle-drop-mode">
                            <Plus size={14} className="mr-1" />{showDrop ? 'Cancel' : 'Drop'}
                        </Button>
                    </div>
                </motion.div>

                {/* Map */}
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                    <Card className="glass-card overflow-hidden mb-4">
                        <div className="h-[400px] sm:h-[500px]" data-testid="ar-map-container">
                            <MapContainer center={center} zoom={14} className="h-full w-full rounded-xl" style={{ background: '#1a1a2e' }}>
                                <TileLayer
                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
                                    url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                                />

                                {/* User location */}
                                {userPos && (
                                    <Circle center={userPos} radius={50} pathOptions={{ color: '#1E90FF', fillColor: '#1E90FF', fillOpacity: 0.2 }} />
                                )}

                                {/* Waypoints */}
                                {waypoints.map(wp => (
                                    <Marker key={wp.waypoint_id} position={[wp.lat, wp.lng]} icon={getIcon(wp.type)}>
                                        <Popup>
                                            <div className="text-center min-w-[160px]">
                                                <strong>{wp.title}</strong>
                                                <br /><span className="text-xs">+{wp.reward_boltz} Boltz | +{wp.reward_xp} XP</span>
                                                {wp.description && <><br /><span className="text-xs text-gray-500">{wp.description}</span></>}
                                                <br />
                                                {collectedIds.has(wp.waypoint_id) ? (
                                                    <span className="text-xs text-green-500 font-bold">Collected</span>
                                                ) : (
                                                    <button
                                                        onClick={() => collectWaypoint(wp.waypoint_id)}
                                                        disabled={collecting === wp.waypoint_id}
                                                        className="mt-1 px-3 py-1 bg-blue-600 text-white text-xs rounded-full"
                                                    >
                                                        {collecting === wp.waypoint_id ? '...' : 'Collect'}
                                                    </button>
                                                )}
                                            </div>
                                        </Popup>
                                    </Marker>
                                ))}

                                {/* Drop mode */}
                                {showDrop && <LocationMarker position={dropPos} setPosition={setDropPos} />}
                            </MapContainer>
                        </div>
                    </Card>
                </motion.div>

                {/* Drop Form */}
                {showDrop && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}>
                        <Card className="glass-card mb-4 border-primary/20">
                            <CardContent className="p-4 space-y-3">
                                <h3 className="font-display text-sm text-primary">DROP A WAYPOINT</h3>
                                <p className="text-xs text-muted-foreground">Tap the map to set location. Costs Boltz from your balance.</p>
                                <Input value={dropTitle} onChange={e => setDropTitle(e.target.value)} placeholder="Waypoint name (optional)" data-testid="drop-title" />
                                <div className="flex gap-2">
                                    <Input type="number" min="1" max="50" value={dropReward} onChange={e => setDropReward(e.target.value)}
                                        placeholder="Reward (Boltz)" className="w-32 font-mono" data-testid="drop-reward" />
                                    <Button onClick={dropWaypoint} disabled={dropping || !dropPos} className="flex-1 btn-primary" data-testid="drop-waypoint-btn">
                                        <MapPin size={16} className="mr-1" />{dropping ? 'Dropping...' : 'Drop Waypoint'}
                                    </Button>
                                </div>
                                {dropPos && <p className="text-xs text-muted-foreground font-mono">Location: {dropPos[0].toFixed(5)}, {dropPos[1].toFixed(5)}</p>}
                            </CardContent>
                        </Card>
                    </motion.div>
                )}

                {/* Recent Collections */}
                {myCollections.length > 0 && (
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                        <h3 className="font-display text-sm text-muted-foreground mb-2">RECENT COLLECTIONS</h3>
                        <div className="space-y-2">
                            {myCollections.slice(0, 5).map(c => (
                                <Card key={c.collection_id} className="glass-card">
                                    <CardContent className="p-3 flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-full bg-neon-gold/10 flex items-center justify-center">
                                                <Lightning size={16} className="text-neon-gold" />
                                            </div>
                                            <div>
                                                <p className="text-sm font-mono text-neon-gold">+{c.reward_boltz} Boltz</p>
                                                <p className="text-[10px] text-muted-foreground">{c.distance_m}m away</p>
                                            </div>
                                        </div>
                                        <p className="text-[10px] text-muted-foreground">{new Date(c.collected_at).toLocaleDateString()}</p>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </motion.div>
                )}
            </div>
        </Layout>
    );
}
